/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React, { useState, useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  SoftwareDevelopment,
  SoftwareConsultancyTraining,
  OSSComplianceSecurity,
  SoftwareQualityAssurance,
  DigitalMarketing,
  ECommerce,
  RemoteTeams,
  Recruitment,
  SoftwareDevelopmentWhite,
  SoftwareConsultancyTrainingWhite,
  OSSComplianceSecurityWhite,
  SoftwareQualityAssuranceWhite,
  DigitalMarketingWhite,
  ECommerceWhite,
  RemoteTeamsWhite,
  RecruitmentWhite,
} from "../assets/svg";

const Services = () => {
  const { t } = useTranslation();

  // Memoize servicesData to prevent unnecessary re-creation
  const servicesData = useMemo(
    () => [
      {
        title: t("servicesSection.softwareDev"),
        description: t("servicesSection.softwareDevDesc"),
        icon_B: SoftwareDevelopment,
        icon_W: SoftwareDevelopmentWhite,
        hover: false,
        hrefLink: "/service/software-dev",
        color: "from-blue-500 to-blue-600",
      },
      {
        title: t("servicesSection.softwareQuality"),
        description: t("servicesSection.softwareQualityDesc"),
        icon_B: SoftwareQualityAssurance,
        icon_W: SoftwareQualityAssuranceWhite,
        hover: false,
        hrefLink: "/service/software-quality-assurance",
        color: "from-indigo-500 to-indigo-600",
      },
      {
        title: t("servicesSection.ossCompliance"),
        description: t("servicesSection.ossComplianceDesc"),
        icon_B: OSSComplianceSecurity,
        icon_W: OSSComplianceSecurityWhite,
        hover: false,
        hrefLink: "/service/oss-compliance-and-security",
        color: "from-purple-500 to-purple-600",
      },
      {
        title: t("servicesSection.consultancy"),
        description: t("servicesSection.consultancyDesc"),
        icon_B: SoftwareConsultancyTraining,
        icon_W: SoftwareConsultancyTrainingWhite,
        hover: false,
        hrefLink: "/service/software-consultacy-and-training",
        color: "from-teal-500 to-teal-600",
      },
      {
        title: t("servicesSection.digitalMarketing"),
        description: t("servicesSection.digitalMarketingDesc"),
        icon_B: DigitalMarketing,
        icon_W: DigitalMarketingWhite,
        hover: false,
        hrefLink: "/service/digital-marketing",
        color: "from-pink-500 to-pink-600",
      },
      {
        title: t("servicesSection.ecommerce"),
        description: t("servicesSection.ecommerceDesc"),
        icon_B: ECommerce,
        icon_W: ECommerceWhite,
        hover: false,
        hrefLink: "/service/ecommerce",
        color: "from-orange-500 to-orange-600",
      },
      {
        title: t("servicesSection.remoteTeams"),
        description: t("servicesSection.remoteTeamsDesc"),
        icon_B: RemoteTeams,
        icon_W: RemoteTeamsWhite,
        hover: false,
        hrefLink: "/service/remote-teams",
        color: "from-red-500 to-red-600",
      },
      {
        title: t("servicesSection.recruitment"),
        description: t("servicesSection.recruitmentDesc"),
        icon_B: Recruitment,
        icon_W: RecruitmentWhite,
        hover: false,
        hrefLink: "/service/recruitment",
        color: "from-yellow-500 to-yellow-600",
      },
    ],
    [t]
  );

  const [services, setServices] = useState(servicesData);

  const handleMouseEnter = (index) => {
    setServices((prevServices) => {
      const updatedServices = [...prevServices];
      updatedServices[index].hover = true;
      return updatedServices;
    });
  };

  const handleMouseLeave = (index) => {
    setServices((prevServices) => {
      const updatedServices = [...prevServices];
      updatedServices[index].hover = false;
      return updatedServices;
    });
  };

  useEffect(() => {
    setServices(servicesData);
  }, [servicesData]);

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t("servicesSection.heading")}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t("servicesSection.subHeading")}
          </p>
        </motion.div>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid key={index} item xs={12} md={6} lg={3}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => (window.location.href = service.hrefLink)}
                className="h-full"
              >
                <div
                  className={`h-full flex flex-col bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer ${
                    service.hover ? `bg-gradient-to-br ${service.color}` : ""
                  }`}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={() => handleMouseLeave(index)}
                >
                  <div className="p-6 flex-grow">
                    <div className="flex justify-center items-center mb-6 h-24">
                      {service.hover ? (
                        <img
                          src={service.icon_W}
                          className="h-20 w-auto transition-transform duration-300 transform hover:scale-110"
                          alt={service.title}
                        />
                      ) : (
                        <img
                          src={service.icon_B}
                          className="h-20 w-auto transition-transform duration-300 transform hover:scale-110"
                          alt={service.title}
                        />
                      )}
                    </div>
                    <h3
                      className={`text-xl font-bold mb-3 text-center ${
                        service.hover ? "text-white" : "text-gray-800"
                      }`}
                    >
                      {service.title}
                    </h3>
                    <p
                      className={`text-sm ${
                        service.hover ? "text-white/90" : "text-gray-600"
                      }`}
                    >
                      {service.description}
                    </p>
                  </div>
                  <div className="p-4 border-t border-gray-100">
                    <div
                      className={`text-sm font-medium text-center ${
                        service.hover ? "text-white" : "text-blue-600"
                      }`}
                    >
                      Learn more →
                    </div>
                  </div>
                </div>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </div>
    </section>
  );
};

export default Services;
